import UnlimitedInput from '@/components/UnlimitedInput'
import contents from '@/modules/Service/help/calculation-methods-help.md'
import AddIcons from '@/icons/AddIcons'
import AnalyticsUpIcons from '@/icons/AnalyticsUpIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import InfoIcons from '@/icons/InfoIcons'
import { PRICE_CONFIG } from '@/modules/Contract/constants'
import {
    getServiceConfigoptionPrices,
    updateServiceConfigoptionPrices,
} from '@/modules/Service/services'
import { ServiceConfigoption } from '@/modules/Service/types'
import { useAppStore } from '@/stores/useAppStore'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Path, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, GridHookForm, Typogaphy } from 'vnetwork-ui'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import configPriceRangesSchema from './schema'
import ConfigPriceRangesViewOnly from './ViewOnly'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

// Custom Price Ranges List Component
interface PriceRangesListProps {
    configIndex: number
    formControl: any
}

const PriceRangesList: React.FC<PriceRangesListProps> = ({
    configIndex,
    formControl,
}) => {
    const { t } = useTranslation()

    // Get current price ranges value
    const currentRanges =
        formControl.watch(
            `service_configoptions.${configIndex}.price_ranges`
        ) || []

    const addRange = () => {
        const updatedRanges = [...currentRanges]

        // Smart logic for new range
        let newRange
        if (updatedRanges.length === 0) {
            // First range starts from 0
            newRange = {
                price_start: '0',
                price_end: '999999999',
                price_per_unit: '',
            }
        } else {
            const lastRange = updatedRanges[updatedRanges.length - 1]

            // If last range has unlimited (999999999), create a proper intermediate range
            if (
                lastRange.price_end === '999999999' ||
                Number(lastRange.price_end) >= 999999999
            ) {
                // Suggest a reasonable intermediate value based on price_start
                const lastStart = Number(lastRange.price_start) || 0
                const suggestedEnd = lastStart + Math.max(1000, lastStart * 0.5) // Add 50% or minimum 1000

                // Update previous range to end at suggested value
                updatedRanges[updatedRanges.length - 1] = {
                    ...lastRange,
                    price_end: suggestedEnd.toString(),
                }

                // New range starts where previous ends, keeps same price_per_unit as suggestion
                newRange = {
                    price_start: suggestedEnd.toString(),
                    price_end: '999999999',
                    price_per_unit: lastRange.price_per_unit || '',
                }
            } else {
                // Normal case: start where last range ended
                const nextStart = Number(lastRange.price_end).toString()
                newRange = {
                    price_start: nextStart,
                    price_end: '999999999',
                    price_per_unit: lastRange.price_per_unit || '',
                }
            }
        }

        updatedRanges.push(newRange)
        formControl.setValue(
            `service_configoptions.${configIndex}.price_ranges`,
            updatedRanges,
            {
                shouldDirty: true,
                shouldTouch: true,
            }
        )
    }

    const removeLastRange = () => {
        if (currentRanges.length === 0) return

        // Only remove the last range
        const updatedRanges = [...currentRanges]
        updatedRanges.pop()

        // If there are still ranges left, set the new last range price_end to 999999999
        if (updatedRanges.length > 0) {
            updatedRanges[updatedRanges.length - 1].price_end = '999999999'
        }

        formControl.setValue(
            `service_configoptions.${configIndex}.price_ranges`,
            updatedRanges,
            {
                shouldDirty: true,
                shouldTouch: true,
            }
        )
    }

    const updateRange = (
        rangeIndex: number,
        field: string,
        value: string | null
    ) => {
        const updatedRanges = [...currentRanges]
        updatedRanges[rangeIndex] = {
            ...updatedRanges[rangeIndex],
            [field]: value,
        }

        // Auto-update next range's price_start when current range's price_end changes
        if (field === 'price_end' && value !== null && value !== '') {
            const nextRangeIndex = rangeIndex + 1
            // Check if there's a next range
            if (nextRangeIndex < updatedRanges.length) {
                updatedRanges[nextRangeIndex] = {
                    ...updatedRanges[nextRangeIndex],
                    price_start: value,
                }

                // Also cascade update subsequent ranges if they exist
                // Each range starts where the previous one ends
                for (
                    let i = nextRangeIndex + 1;
                    i < updatedRanges.length - 1;
                    i++
                ) {
                    const prevRange = updatedRanges[i - 1]
                    if (
                        prevRange.price_end &&
                        prevRange.price_end !== '999999999'
                    ) {
                        updatedRanges[i] = {
                            ...updatedRanges[i],
                            price_start: prevRange.price_end,
                        }
                    }
                }
            }
        }

        formControl.setValue(
            `service_configoptions.${configIndex}.price_ranges`,
            updatedRanges,
            {
                shouldDirty: true,
                shouldTouch: true,
            }
        )
    }

    return (
        <div className="">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4 text-xs font-medium text-gray-600 mb-2">
                <span>{t('services.tier')}</span>
                <span>{t('services.usage-from')}</span>
                <span>{t('services.usage-to')}</span>
                <span>{t('services.price-per-unit')}</span>
            </div>

            {/* Empty State */}
            {currentRanges.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                            />
                        </svg>
                    </div>
                    <p className="text-sm font-medium mb-1">
                        {t('services.no-ranges')}
                    </p>
                </div>
            )}

            {/* Price Ranges */}
            {currentRanges.map((range: any, rangeIndex: number) => (
                <div
                    key={`range-${rangeIndex}-${range?.price_start ?? ''}-${range?.price_end ?? ''}`}
                    className="grid grid-cols-4 gap-4 items-end"
                >
                    <GridHookForm
                        actions={[]}
                        formControl={formControl}
                        subForm
                        data={[
                            {
                                className: 'col-span-1',
                            },
                            {
                                formItemProps: {
                                    name: `service_configoptions.${configIndex}.price_ranges.${rangeIndex}.price_start` as any,
                                    type: 'number',
                                    placeholder: t('services.usage-from'),
                                },
                            },
                            {
                                className:
                                    rangeIndex === currentRanges.length - 1
                                        ? 'hidden'
                                        : '',
                                renderEditComponent: () => (
                                    <input
                                        type="number"
                                        placeholder={t('services.usage-to')}
                                        value={range?.price_end || ''}
                                        onChange={(e) => {
                                            const value = e.target.value
                                            updateRange(
                                                rangeIndex,
                                                'price_end',
                                                value
                                            )
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                ),
                            },
                            {
                                className:
                                    rangeIndex !== currentRanges.length - 1
                                        ? 'hidden'
                                        : '',
                                renderEditComponent: () => (
                                    <UnlimitedInput
                                        value={range?.price_end}
                                        onChange={(value) =>
                                            updateRange(
                                                rangeIndex,
                                                'price_end',
                                                value
                                            )
                                        }
                                        placeholder={t('services.usage-to')}
                                        type="number"
                                    />
                                ),
                            },
                            {
                                formItemProps: {
                                    name: `service_configoptions.${configIndex}.price_ranges.${rangeIndex}.price_per_unit` as any,
                                    type: 'number',
                                    placeholder: t('services.price-per-unit'),
                                },
                            },
                        ]}
                    />
                </div>
            ))}
            <div className="mt-2 gap-4 grid grid-cols-4 justify-end items-end">
                <div className="col-span-2" />
                <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={removeLastRange}
                    disabled={currentRanges.length === 0}
                    startIcon={<DeleteOutlineIcons />}
                    className="w-full border-1 border-dashed border-gray-300 rounded-sm text-gray-500 hover:border-red-300 hover:text-c-error transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {t('services.remove-range')}
                </Button>
                <Button
                    type="button"
                    size="small"
                    variant="outlined"
                    onClick={addRange}
                    startIcon={<AddIcons />}
                    className="w-full border-1 border-dashed rounded-sm border-blue-300 text-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                    {t('services.add-range')}
                </Button>
            </div>
        </div>
    )
}

interface ConfigPriceRangesProps {
    serviceID: number
    show_price_list?: boolean
    viewOnly?: boolean
    /** Compact mode: no outer shadow/border/padding, smaller title */
    compact?: boolean
}

interface ConfigPriceRangesForm {
    service_configoptions: ServiceConfigoption[]
}

const ConfigPriceRanges = ({
    serviceID,
    show_price_list = false,
    viewOnly = true,
    compact = false,
}: ConfigPriceRangesProps) => {
    const [isEditMode, setIsEditMode] = useState(false)
    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_CONFIGOPTIONS_PRICES, serviceID],
        queryFn: () => getServiceConfigoptionPrices({ serviceID }),
    })

    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const { setIsShowHelp, setHelpContent, isShowHelp } = useAppStore(
        (state) => state
    )

    const { mutateAsync: updatePrices, isPending: isUpdating } =
        useMutationWithHandler(
            {
                mutationFn: updateServiceConfigoptionPrices,
            },
            {
                successMessage: t('update-success'),
                reactQueryKeys: QUERY_KEYS.SERVICES,
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: [
                            QUERY_KEYS.SERVICES_CONFIGOPTIONS_PRICES,
                            serviceID,
                        ],
                    })
                },
            }
        )

    // Transform data helper function
    const transformDataToFormValues = useCallback(
        (apiData: ServiceConfigoption[]) => ({
            service_configoptions:
                apiData
                    ?.filter((configoption) => !!configoption?.statementable)
                    ?.map((configoption) => {
                        return {
                            ...configoption,
                            method: {
                                calc_amount_method: !configoption?.method
                                    ?.calc_amount_method
                                    ? 'tierd'
                                    : configoption?.method?.calc_amount_method,
                            },
                            price_ranges:
                                configoption?.price_ranges
                                    ?.slice()
                                    .sort((a, b) => {
                                        const aStart =
                                            Number(a.price_start) || 0
                                        const bStart =
                                            Number(b.price_start) || 0
                                        return aStart - bStart
                                    })
                                    .map((range) => {
                                        return {
                                            ...range,
                                            price_start: Number(
                                                range.price_start
                                            ),
                                            price_end: Number(range.price_end),
                                            price_per_unit: Number(
                                                range.price_per_unit
                                            ),
                                        }
                                    }) || [],
                        }
                    }) || [],
        }),
        []
    )

    const formControl = useForm<ConfigPriceRangesForm>({
        defaultValues: transformDataToFormValues(data || []),
        // @ts-ignore
        resolver: yupResolver(configPriceRangesSchema),
    })
    const { fields } = useFieldArray({
        control: formControl.control,
        name: 'service_configoptions',
    })

    const onSave = async (values: ConfigPriceRangesForm) => {
        const payload = values.service_configoptions?.map((configoption) => {
            return {
                price_exceed_id: configoption?.price_exceed_id || -1,
                // @ts-ignore
                method: !configoption?.method?.calc_amount_method
                    ? 'tierd'
                    : configoption?.method?.calc_amount_method,
                price_ranges:
                    configoption.price_ranges?.map((range) => {
                        return {
                            ...range,
                            price_start: Number(range.price_start),
                            price_end: Number(range.price_end),
                            price_per_unit: Number(range.price_per_unit),
                            currency_code: 'vn',
                        }
                    }) || [],
                service_configoption_id: configoption.service_configoption_id,
            }
        })

        await updatePrices({
            serviceID,
            service_configoptions: payload,
        })
    }

    useEffect(() => {
        if (!data) return

        // Sort and validate price_ranges for each service_configoption
        const processedData = data.map((configoption) => {
            let sortedPriceRanges = configoption.price_ranges || []

            // Sort by price_start ascending
            sortedPriceRanges = sortedPriceRanges.sort((a, b) => {
                const aStart = Number(a.price_start) || 0
                const bStart = Number(b.price_start) || 0
                return aStart - bStart
            })

            // Validate and fix range continuity
            sortedPriceRanges = sortedPriceRanges.map((range, index) => {
                const updatedRange = { ...range }

                // Ensure first range starts from 0
                if (
                    index === 0 &&
                    (!updatedRange.price_start ||
                        Number(updatedRange.price_start) !== 0)
                ) {
                    updatedRange.price_start = 0
                }

                // Ensure last range ends with unlimited
                if (index === sortedPriceRanges.length - 1) {
                    updatedRange.price_end = 999999999
                }

                // Ensure continuity between ranges
                if (index > 0 && sortedPriceRanges[index - 1]) {
                    const prevEnd =
                        Number(sortedPriceRanges[index - 1].price_end) || 0
                    updatedRange.price_start = prevEnd
                }

                return updatedRange
            })

            return {
                ...configoption,
                price_ranges: sortedPriceRanges,
            }
        })

        // Transform and reset form with new data
        const formValues = transformDataToFormValues(processedData)
        formControl.reset(formValues)
    }, [data, formControl, transformDataToFormValues])

    const handleShowCalcMethodHelp = () => {
        setIsShowHelp(!isShowHelp)
        setHelpContent(contents)
    }

    const renderCalcMethodHelpButton = () => (
        <Button
            type="button"
            size="small"
            variant="outlined"
            color="info"
            startIcon={<InfoIcons />}
            onClick={handleShowCalcMethodHelp}
            className="border-dashed"
        >
            {t('services.calc-method.help')}
        </Button>
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    // ViewOnly mode (default)
    if (viewOnly && !isEditMode) {
        return (
            <ConfigPriceRangesViewOnly
                data={data || []}
                serviceID={serviceID}
                show_price_list={show_price_list}
                onEditMode={() => setIsEditMode(true)}
            />
        )
    }

    return (
        <div className={compact ? '' : 'mt-2'}>
            <div
                className={
                    compact
                        ? ''
                        : 'bg-white rounded-lg shadow-sm border border-gray-200 p-4'
                }
            >
                {!compact && (
                    <div className="border-b border-gray-200 pb-2">
                        <div className="flex items-center justify-between">
                            <div>
                                <Typogaphy className="text-lg font-semibold text-gray-800">
                                    {t('services.price-exceed.title')}
                                </Typogaphy>
                                <Typogaphy className="text-sm text-gray-600">
                                    {t('services.price-exceed.subtitle')}
                                </Typogaphy>
                            </div>
                            <div className="flex items-center gap-2">
                                <Typogaphy className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                    {data?.length || 0}{' '}
                                    {t('services.configurations')}
                                </Typogaphy>
                            </div>
                        </div>
                    </div>
                )}
                <GridHookForm
                    formControl={formControl}
                    data={[
                        {
                            className: 'col-span-4',
                            renderEditComponent: () => (
                                <>
                                    {fields?.length > 0 &&
                                        fields?.map((field, index) => (
                                            <div key={field.id}>
                                                <GridHookForm
                                                    subForm
                                                    actions={[]}
                                                    data={[
                                                        {
                                                            className:
                                                                'col-span-4',
                                                            renderEditComponent:
                                                                () => (
                                                                    <div className="flex items-center justify-between">
                                                                        <div className="flex items-center gap-2">
                                                                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                                                <span className="text-blue-600 font-semibold text-sm">
                                                                                    {index +
                                                                                        1}
                                                                                </span>
                                                                            </div>
                                                                            <div>
                                                                                <Typogaphy className="font-medium text-gray-800">
                                                                                    {t(
                                                                                        field.name
                                                                                    )}
                                                                                </Typogaphy>
                                                                                <Typogaphy className="text-xs text-gray-500">
                                                                                    {t(
                                                                                        'services.config-option'
                                                                                    )}
                                                                                </Typogaphy>
                                                                            </div>
                                                                        </div>
                                                                        <div className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                                                                            {t(
                                                                                'services.price-exceed.active'
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ),
                                                        },
                                                        {
                                                            className:
                                                                'col-span-4',
                                                            renderEditComponent:
                                                                () => (
                                                                    <div className="space-y-2">
                                                                        <div className="relative">
                                                                            <GridHookForm
                                                                                formControl={
                                                                                    formControl
                                                                                }
                                                                                subForm
                                                                                actions={[]}
                                                                                data={[
                                                                                    {
                                                                                        formItemProps:
                                                                                            {
                                                                                                name: `service_configoptions.[${index}].method.calc_amount_method` as Path<ConfigPriceRangesForm>,
                                                                                                type: 'select',
                                                                                                options:
                                                                                                    PRICE_CONFIG(
                                                                                                        t
                                                                                                    ),
                                                                                                allowClear: false,
                                                                                                placeholder:
                                                                                                    t(
                                                                                                        'form-of-comparison'
                                                                                                    ),
                                                                                            },
                                                                                        className:
                                                                                            'col-span-3',
                                                                                    },
                                                                                    {
                                                                                        className:
                                                                                            'col-span-1 flex justify-end items-center',
                                                                                        renderEditComponent:
                                                                                            renderCalcMethodHelpButton,
                                                                                    },
                                                                                ]}
                                                                            />
                                                                        </div>
                                                                        {show_price_list && (
                                                                            <div className="space-y-2">
                                                                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                                    <div className="flex items-center gap-2 mb-3">
                                                                                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                                                                                            <AnalyticsUpIcons className="w-4 h-4" />
                                                                                        </div>
                                                                                        <Typogaphy className="font-medium text-blue-900 text-sm">
                                                                                            {t(
                                                                                                'services.pricing-tiers'
                                                                                            )}
                                                                                        </Typogaphy>
                                                                                    </div>

                                                                                    <div className="bg-white rounded-lg border border-blue-200 p-4">
                                                                                        {/* Custom Price Ranges Implementation */}
                                                                                        <PriceRangesList
                                                                                            configIndex={
                                                                                                index
                                                                                            }
                                                                                            formControl={
                                                                                                formControl
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ),
                                                        },
                                                    ]}
                                                    formControl={formControl}
                                                />
                                            </div>
                                        ))}
                                </>
                            ),
                        },
                    ]}
                    className="mt-2"
                    onSubmit={onSave}
                    actions={[]}
                />
                <div
                    className={`border-t border-gray-200 flex justify-between items-center relative z-10 ${compact ? 'pt-3' : 'p-4 bg-gray-50'}`}
                >
                    <div className="flex items-center gap-4">
                        <div className="text-sm text-gray-600">
                            {formControl.formState.isDirty ? (
                                <span className="text-amber-600 font-medium">
                                    {t('services.unsaved-changes')}
                                </span>
                            ) : (
                                <span className="text-green-600 font-medium">
                                    {t('all-changes-saved')}
                                </span>
                            )}
                        </div>
                        {viewOnly && isEditMode && (
                            <Button
                                type="button"
                                variant="text"
                                color="info"
                                size="small"
                                onClick={() => setIsEditMode(false)}
                            >
                                ← {t('cancel')}
                            </Button>
                        )}
                    </div>
                    <Button
                        disabled={!formControl.formState.isDirty || isUpdating}
                        variant="contained"
                        color="primary"
                        type="submit"
                        loading={isUpdating}
                        className="min-w-[120px]"
                        onClick={(e) => {
                            e.preventDefault()
                            formControl.handleSubmit(onSave as any)(e as any)
                        }}
                    >
                        {isUpdating ? t('saving') : t('save')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default ConfigPriceRanges
